import { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useRouter } from '@tanstack/react-router'

/**
 * Состояние фильтров каталога
 */
export interface CatalogFiltersState {
  category?: string
  subcategory?: string
  sort?: string
  search?: string
}

const STORAGE_KEY_PREFIX = 'catalog-filters-'

/**
 * Сохраняет фильтры каталога в sessionStorage.
 * При возврате в каталог (back navigation) — восстанавливает последние фильтры.
 * При прямом переходе в каталог — начинает с чистого состояния.
 *
 * Работает в паре с URL query params TanStack Router:
 * URL — источник истины, sessionStorage — бэкап при уходе со страницы.
 */
export function useCatalogFilters(
  section: string,
  currentFilters: CatalogFiltersState,
) {
  const navigate = useNavigate()
  const router = useRouter()
  const hasRestoredRef = useRef(false)
  const storageKey = `${STORAGE_KEY_PREFIX}${section}`

  // Сохраняем фильтры в sessionStorage перед уходом со страницы
  useEffect(() => {
    const saveFilters = () => {
      const filtersToSave: CatalogFiltersState = {}
      if (currentFilters.category) filtersToSave.category = currentFilters.category
      if (currentFilters.subcategory) filtersToSave.subcategory = currentFilters.subcategory
      if (currentFilters.sort && currentFilters.sort !== 'popular') filtersToSave.sort = currentFilters.sort
      if (currentFilters.search) filtersToSave.search = currentFilters.search

      if (Object.keys(filtersToSave).length > 0) {
        sessionStorage.setItem(storageKey, JSON.stringify(filtersToSave))
      } else {
        sessionStorage.removeItem(storageKey)
      }
    }

    const unsubscribe = router.subscribe('onBeforeNavigate', saveFilters)
    window.addEventListener('beforeunload', saveFilters)

    return () => {
      unsubscribe()
      window.removeEventListener('beforeunload', saveFilters)
    }
  }, [router, storageKey, currentFilters])

  // Восстанавливаем фильтры при возврате на страницу (back navigation)
  useEffect(() => {
    if (hasRestoredRef.current) return
    hasRestoredRef.current = true

    // Если URL уже содержит фильтры — не перезаписываем (пользователь пришёл по ссылке)
    const hasUrlFilters = currentFilters.category || currentFilters.subcategory
      || (currentFilters.sort && currentFilters.sort !== 'popular') || currentFilters.search
    if (hasUrlFilters) return

    // Пробуем восстановить из sessionStorage
    const saved = sessionStorage.getItem(storageKey)
    if (!saved) return

    try {
      const filters: CatalogFiltersState = JSON.parse(saved)
      if (Object.keys(filters).length === 0) return

      // Восстанавливаем фильтры в URL (replace чтобы не засорять историю)
      navigate({
        to: '/catalog/$section',
        params: { section: section as 'tea' | 'teaware' },
        search: filters as Record<string, string>,
        replace: true,
      })
    } catch {
      sessionStorage.removeItem(storageKey)
    }
  }, []) // Только при монтировании

  // Очистка фильтров
  const clearFilters = useCallback(() => {
    sessionStorage.removeItem(storageKey)
    navigate({
      to: '/catalog/$section',
      params: { section: section as 'tea' | 'teaware' },
      search: {},
      replace: true,
    })
  }, [navigate, section, storageKey])

  return { clearFilters }
}
